/**
 * Nav gating primitives — the shared types + role/feature predicates.
 *
 * The navigation DATA MODEL lives in `lib/surfaces.ts` (docs/71 §7): two
 * sides (Me | Manage) plus the global Inbox, one route → side table and
 * the module strips keyed by (side, module). What stays here is the
 * vocabulary that model and every module shell share:
 *
 *  - `NavItem` / `NavSection` — the item + section shapes.
 *  - `roleAllowed` / `featureAllowed` — the two gate predicates
 *    (surfaces.ts filters items through these; gate semantics unchanged).
 *  - `isHRAdmin` / `isAccountingAdmin` + the `HR_ADMINS` /
 *    `ACCOUNTING_ADMINS` / `MANAGER_PLUS` role sets — the canonical
 *    predicates every module's role-gate imports (avoids cross-module
 *    role-set drift; see the G7.5 audit note below).
 *
 * A failing gate = item ABSENT from the DOM (the CLAUDE.md feature-gate
 * rule); the backend re-checks every request, so this is presentation
 * only. `feature` mirrors `tenants.plan.features` JSONB; `roles:
 * undefined` = any authenticated role.
 */

import type { LucideIcon } from "lucide-react";
import type { Feature, Role } from "@/types/auth";

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Tenant plan feature required. Omit for always-available items. */
  feature?: Feature;
  /**
   * OR-gate: the item shows when the tenant has ANY ONE of these
   * features. Use instead of `feature` when a single entry fronts more
   * than one plan module (e.g. "Time & Leave" covers both
   * `leave_management` and `time_tracking`). Mutually exclusive with
   * `feature`; if both are set, `anyFeature` wins.
   */
  anyFeature?: Feature[];
  /** Roles allowed to see this item. Omit = any authenticated role. */
  roles?: Role[];
  /**
   * A per-person condition the menu learns at runtime (not a role or a
   * plan feature): the item shows only when the caller passes this flag
   * to the surface helpers (lib/surfaces.ts). "interviewer" = the person
   * has interviews assigned (wave-B: "My interviews" for anyone, not only
   * HR).
   */
  when?: NavFlag;
  /**
   * The module this item opens (lib/surfaces.ts ROUTE_TABLE). The sidebar
   * marks an item active when the current route belongs to its module on
   * the same side, so a sub-page (/leave/comp-off) lights "Leave" without
   * any URL-prefix guessing.
   */
  module?: string;
  /** Query string for the target (e.g. the ICC's complaint register). */
  search?: Readonly<Record<string, string>>;
}

/**
 * Runtime per-person menu conditions (see NavItem.when).
 *  - "interviewer": has interviews assigned (Inbox → Interviews);
 *  - "approver": can decide someone's request (Inbox → Approvals);
 *  - "projectTime": the company tracks time against client projects
 *    (Me → Attendance → Timesheets);
 *  - "reviewer": has a performance review to write (Inbox → Reviews).
 */
export type NavFlag = "interviewer" | "approver" | "projectTime" | "reviewer";

export interface NavSection {
  /** Section heading, or null for the ungrouped top items. */
  title: string | null;
  items: NavItem[];
  /**
   * A collapsible group (Apple: "group hierarchy with disclosure
   * controls"), e.g. Books. `defaultOpenFor` lists the roles that see it
   * expanded on first visit; everyone else sees it collapsed until they
   * open it (remembered per browser) or one of its items is active.
   */
  collapsible?: { readonly id: string; readonly defaultOpenFor: readonly Role[] };
}

// Roles that can administer HR-wide config across a tenant.
export const HR_ADMINS: Role[] = ["company_admin", "hr_manager"];

// isHRAdmin is the single canonical predicate every module's role-gate
// imports. Avoids the cross-module coupling that bit us in G7.5 audit
// M-1 (change-request module importing isBulkImportAdmin to ask "is
// this user HR?" — semantically identical today but if bulk-import's
// role set ever tightens, change-request HR powers silently drift).
export function isHRAdmin(role: Role | undefined): boolean {
  return role !== undefined && HR_ADMINS.includes(role);
}

// Roles that can administer the Accounting module (G_ACC1+ — contacts,
// items, accounts, ledger, periods, audit). Mirrors the backend
// `accountingRole = RequireRole(company_admin, hr_manager, accountant)`
// composed by router.go for every /api/v1/accounting/* route. The
// HR-admin set is widened to include `accountant` — a CA/bookkeeper who
// is read+write on books-of-account but cannot touch HR/POSH/Performance.
export const ACCOUNTING_ADMINS: Role[] = [
  "company_admin",
  "hr_manager",
  "accountant",
];

export function isAccountingAdmin(role: Role | undefined): boolean {
  return role !== undefined && ACCOUNTING_ADMINS.includes(role);
}

// Roles allowed to reach Manage's team/manager items (a
// manager sees team surfaces even without HR-admin powers). People +
// Attendance-team use this widened set; every deeper Run item keeps its
// own tighter role gate (HR_ADMINS / ACCOUNTING_ADMINS).
export const MANAGER_PLUS: Role[] = ["company_admin", "hr_manager", "manager"];

// Manage → POSH: HR runs the compliance side, an Internal Committee member
// works the complaints (the ICC used to land on an empty sidebar).
export const POSH_MANAGERS: Role[] = ["company_admin", "hr_manager", "icc_member"];

/** True if `role` is permitted to see an item (undefined roles = all). */
export function roleAllowed(item: NavItem, role: Role | undefined): boolean {
  if (!item.roles) return true;
  if (!role) return false;
  return item.roles.includes(role);
}

/**
 * True if `item` passes its feature gate. Supports both the single
 * `feature` gate and the `anyFeature` OR-gate (ANY one present → shown).
 * `anyFeature` takes precedence when both are set; no gate = always on.
 */
export function featureAllowed(
  item: NavItem,
  hasFeature: (f: string) => boolean,
): boolean {
  if (item.anyFeature) return item.anyFeature.some(hasFeature);
  if (item.feature !== undefined) return hasFeature(item.feature);
  return true;
}

/**
 * The plan feature that carries the whole WorkRight platform beyond POSH
 * and the people list (migration 162). Free, Pro and the normal trial have
 * it; a POSH only company does not.
 */
export const HR_PLATFORM_FEATURE = "hr_platform";

/**
 * True for a POSH only company: its /me.features list has arrived and does
 * NOT carry `hr_platform`. A missing list (signed out, /me still loading)
 * is never POSH only, so the shell does not flash the POSH only view while
 * the session loads. An empty list IS POSH only (a POSH only company whose
 * POSH has lapsed resolves to no features at all).
 */
export function isPoshOnly(features: readonly string[] | null | undefined): boolean {
  if (!Array.isArray(features)) return false;
  return !features.includes(HR_PLATFORM_FEATURE);
}
